/**
 * Offline Storage Utility for Clock-in/Clock-out
 * Uses IndexedDB to store pending actions when offline
 */

const DB_NAME = 'clockin_offline_db';
const DB_VERSION = 1;
const STORE_NAME = 'pending_actions';

let dbInstance = null;

/**
 * Initialize IndexedDB
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open offline database');
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('Offline database initialized');
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('action_type', 'action_type', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
        store.createIndex('synced', 'synced', { unique: false });
        console.log('Offline store created');
      }
    };
  });
};

/**
 * Save a pending action to IndexedDB
 */
export const savePendingAction = async (action) => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const actionData = {
      ...action,
      created_at: new Date().toISOString(),
      device_time: new Date().toISOString(),
      synced: false,
      sync_attempts: 0
    };
    
    const request = store.add(actionData);
    
    request.onsuccess = () => {
      console.log('Pending action saved:', action.action_type);
      resolve(request.result);
    };
    
    request.onerror = () => {
      console.error('Failed to save pending action');
      reject(request.error);
    };
  });
};

/**
 * Get all pending (unsynced) actions
 */
export const getPendingActions = async () => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.getAll(false);
    
    request.onsuccess = () => {
      // Sort by created_at to maintain order
      const actions = request.result.sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      resolve(actions);
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Mark an action as synced
 */
export const markActionSynced = async (id, serverResponse = null) => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const action = getRequest.result;
      if (action) {
        action.synced = true;
        action.synced_at = new Date().toISOString();
        action.server_response = serverResponse;
        
        const updateRequest = store.put(action);
        updateRequest.onsuccess = () => resolve(true);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        resolve(false);
      }
    };
    
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Delete a synced action
 */
export const deleteAction = async (id) => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all synced actions (cleanup)
 */
export const clearSyncedActions = async () => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.openCursor(true); // Get all synced=true
    
    let deleted = 0;
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        deleted++;
        cursor.continue();
      } else {
        console.log(`Cleared ${deleted} synced actions`);
        resolve(deleted);
      }
    };
    
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get count of pending actions
 */
export const getPendingCount = async () => {
  const db = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.count(false);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Check if we're online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Create offline clock-in action
 */
export const createOfflineClockIn = async (location, projectId = null) => {
  // Handle case where location might be unavailable or have placeholder values
  const hasValidLocation = location && 
    (location.latitude !== 0 || location.longitude !== 0);
  
  const action = {
    action_type: 'clock_in',
    latitude: location?.latitude || 0,
    longitude: location?.longitude || 0,
    project_id: projectId,
    offline: true,
    location_available: hasValidLocation,
    offline_location: hasValidLocation ? {
      latitude: location.latitude,
      longitude: location.longitude,
      accuracy: location.accuracy || null,
      timestamp: new Date().toISOString()
    } : {
      latitude: 0,
      longitude: 0,
      accuracy: null,
      timestamp: new Date().toISOString(),
      unavailable: true
    }
  };
  
  return savePendingAction(action);
};

/**
 * Create offline clock-out action
 */
export const createOfflineClockOut = async (location = null) => {
  const action = {
    action_type: 'clock_out',
    offline: true,
    offline_location: location ? {
      latitude: location.latitude,
      longitude: location.longitude,
      accuracy: location.accuracy || null,
      timestamp: new Date().toISOString()
    } : null
  };
  
  return savePendingAction(action);
};

/**
 * Create offline break start action
 */
export const createOfflineBreakStart = async (breakType) => {
  const action = {
    action_type: 'break_start',
    break_type: breakType,
    offline: true
  };
  
  return savePendingAction(action);
};

/**
 * Create offline break end action
 */
export const createOfflineBreakEnd = async () => {
  const action = {
    action_type: 'break_end',
    offline: true
  };
  
  return savePendingAction(action);
};

export default {
  initOfflineDB,
  savePendingAction,
  getPendingActions,
  markActionSynced,
  deleteAction,
  clearSyncedActions,
  getPendingCount,
  isOnline,
  createOfflineClockIn,
  createOfflineClockOut,
  createOfflineBreakStart,
  createOfflineBreakEnd
};
